import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  fetchVoucherProducts,
  initiateVoucherPurchase,
  setSelectedVoucherProduct,
  clearVoucherErrors,
} from '../../../actions/action_vouchers';
import VoucherProductCard from './VoucherProductCard';

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 16px;
  color: #dc2626;
  text-align: center;
  margin: 20px 0;
`;

const NotAvailableMessage = styled.div`
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  color: #92400e;
`;

const CanceledMessage = styled.div`
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  color: #92400e;
  text-align: center;
  margin-bottom: 24px;
`;

const TermsSection = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
  background-color: #f9fafb;
  border-radius: 8px;
`;

const TermsCheckbox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 20px;

  input {
    margin-top: 4px;
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  span {
    font-size: 14px;
    color: #333;
  }

  a {
    color: #22c55e;
    text-decoration: underline;

    &:hover {
      color: #16a34a;
    }
  }
`;

const PurchaseButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background-color: ${(props) => (props.disabled ? '#9ca3af' : '#22c55e')};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #16a34a;
  }
`;

const SelectedProductSummary = styled.div`
  background-color: #ecfdf5;
  border: 2px solid #22c55e;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
`;

const SummaryTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #166534;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #22c55e;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

function VoucherPurchase() {
  const dispatch = useDispatch();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);

  const products = useSelector((state) => state.vouchers.products);
  const productsLoading = useSelector(
    (state) => state.vouchers.productsLoading,
  );
  const productsError = useSelector((state) => state.vouchers.productsError);
  const vouchersAvailable = useSelector(
    (state) => state.vouchers.vouchersAvailable,
  );
  const selectedProduct = useSelector(
    (state) => state.vouchers.selectedProduct,
  );
  const purchaseLoading = useSelector(
    (state) => state.vouchers.purchaseLoading,
  );
  const purchaseError = useSelector((state) => state.vouchers.purchaseError);
  const checkoutUrl = useSelector((state) => state.vouchers.checkoutUrl);
  const voucherTermsUrl = useSelector(
    (state) => state.vouchers.voucherTermsUrl,
  );

  // Initial mount effect
  useEffect(() => {
    // Clear any previously selected product to show product list on re-entry
    dispatch(setSelectedVoucherProduct(null));
    dispatch(fetchVoucherProducts());
    dispatch(clearVoucherErrors());

    // Check if redirected back from canceled checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('canceled') === 'true') {
      setShowCanceled(true);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [dispatch]);

  // Redirect to Stripe checkout when URL is available
  useEffect(() => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl]);

  const handleProductSelect = (product) => {
    dispatch(setSelectedVoucherProduct(product));
    setTermsAccepted(false);
  };

  const handleBackToProducts = () => {
    dispatch(setSelectedVoucherProduct(null));
    setTermsAccepted(false);
    dispatch(clearVoucherErrors());
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const handlePurchase = () => {
    if (!selectedProduct || !termsAccepted) return;
    dispatch(initiateVoucherPurchase(selectedProduct.id, termsAccepted));
  };

  const renderLoading = () => (
    <LoadingWrapper>
      <div className="spinner" />
    </LoadingWrapper>
  );

  const renderNotAvailable = () => (
    <NotAvailableMessage>
      <h3>Vouchers Not Available</h3>
      <p>Vouchers are not currently available for purchase at this studio.</p>
    </NotAvailableMessage>
  );

  const renderProducts = () => (
    <>
      <PageHeader>
        <PageTitle>Purchase a Voucher</PageTitle>
        <PageSubtitle>
          Buy credit to use on your orders. Some vouchers include bonus credit!
        </PageSubtitle>
      </PageHeader>

      <ProductsGrid>
        {products.map((product) => (
          <VoucherProductCard
            key={product.id}
            product={product}
            onSelect={handleProductSelect}
          />
        ))}
      </ProductsGrid>
    </>
  );

  const renderTermsAndCheckout = () => (
    <TermsSection>
      <BackButton onClick={handleBackToProducts}>
        &larr; Back to voucher options
      </BackButton>

      <SelectedProductSummary>
        <SummaryTitle>{selectedProduct.name}</SummaryTitle>
        <SummaryValue>
          Pay {selectedProduct.purchase_price_formatted} &rarr; Get{' '}
          {selectedProduct.total_value_formatted}
        </SummaryValue>
        {selectedProduct.promo_value_cents > 0 && (
          <div style={{ fontSize: '14px', color: '#166534', marginTop: '4px' }}>
            Includes bonus credit!
          </div>
        )}
      </SelectedProductSummary>
      <TermsCheckbox>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={handleTermsChange}
        />
        <span>
          I understand that this Voucher Credit is intended for Spring Picture
          orders
          {selectedProduct.promo_value_cents > 0 &&
          selectedProduct.promo_expires
            ? `, and bonus credit expires ${selectedProduct.promo_expiration_days} days after purchase`
            : ''}
          .
          {voucherTermsUrl && /^https?:\/\//.test(voucherTermsUrl) && (
            <>
              {' '}
              I agree to the{' '}
              <a href={voucherTermsUrl} target="_blank" rel="noreferrer">
                terms and conditions
              </a>
              .
            </>
          )}
        </span>
      </TermsCheckbox>

      {purchaseError && <ErrorMessage>{purchaseError}</ErrorMessage>}

      <PurchaseButton
        disabled={!termsAccepted || purchaseLoading}
        onClick={handlePurchase}
      >
        {purchaseLoading
          ? 'Processing...'
          : `Pay ${selectedProduct.purchase_price_formatted}`}
      </PurchaseButton>
    </TermsSection>
  );

  if (productsLoading) {
    return <PageWrapper>{renderLoading()}</PageWrapper>;
  }

  if (productsError) {
    return (
      <PageWrapper>
        <ErrorMessage>{productsError}</ErrorMessage>
      </PageWrapper>
    );
  }

  if (!vouchersAvailable || products.length === 0) {
    return <PageWrapper>{renderNotAvailable()}</PageWrapper>;
  }

  return (
    <PageWrapper>
      {showCanceled && (
        <CanceledMessage>
          Purchase was canceled. You can select a voucher to try again.
        </CanceledMessage>
      )}

      {selectedProduct ? renderTermsAndCheckout() : renderProducts()}
    </PageWrapper>
  );
}

export default VoucherPurchase;
